import type { AnyObject } from 'types/AnyObject';
import type { DateRange, SelectedFilters } from 'uikit';

import * as R from 'ramda';
import type { Dispatch, SetStateAction } from 'react';

const filterConverters = {
    updatedAt(selected: string[], value: DateRange) {
        const [gte, lt] = value.map((date, idx) => {
            if (idx === 1 && date) {
                const nextDayDate = new Date(date);

                nextDayDate.setDate(nextDayDate.getDate() + 1);

                return nextDayDate.toISOString();
            }

            return date ? date.toISOString() : undefined;
        });

        return {
            gte,
            lt,
        };
    },
};

const getValueAnd = (fieldName: string, value: any) => {
    const objIn = {
        in: value.map(
            ({ data: { value: itemValue } }: { data: { value: { id?: number } } }) => itemValue?.id ?? itemValue,
        ),
    };

    return fieldName === 'subsidiaryName' ? { subsidiaryId: objIn } : { [fieldName]: objIn };
};

const filtersToWhere = (selectedFilters: SelectedFilters) => {
    const predicates = Object.entries(selectedFilters)
        .filter(([, { selected, value }]) => selected.length && !R.isNil(value) && value.length)
        .map(([fieldName, { selected, value }]) =>
            filterConverters[fieldName as keyof typeof filterConverters]
                ? { [fieldName]: filterConverters[fieldName as keyof typeof filterConverters](selected, value) }
                : getValueAnd(fieldName, value),
        );

    return predicates.length
        ? {
              and: predicates,
          }
        : undefined;
};

export const getWhere = (selectedFilters: SelectedFilters & { searchString: string }) => {
    const { searchString, ...rest } = selectedFilters || {};
    const filtersWhere = filtersToWhere(rest);

    const stringWhere = searchString && {
        or: [
            {
                subsidiaryName: {
                    containsi: searchString,
                },
            },
            {
                name: {
                    containsi: searchString,
                },
            },
            {
                authoredBy: {
                    containsi: searchString,
                },
            },
        ],
    };

    if (stringWhere && filtersWhere && !R.isEmpty(filtersWhere)) {
        return {
            and: [stringWhere, filtersWhere],
        };
    }

    if (stringWhere) {
        return stringWhere;
    }

    if (!R.isEmpty(filtersWhere)) {
        return filtersWhere;
    }

    return undefined;
};

export const SEARCH_STRING = 'projectsSearch';

type TSavedFilterValues = string | Record<string, ({ value: any; data: any } | string)[]>;

export const getValuesFromSaved =
    ({
        setQueryWhere,
        setSearchString,
    }: {
        setQueryWhere: Dispatch<SetStateAction<AnyObject>>;
        setSearchString: Dispatch<SetStateAction<string>>;
    }) =>
    ({ savedValuesArray }: { savedValuesArray: [string, TSavedFilterValues][] | null }) => {
        const valuesFromSaved = savedValuesArray?.reduce((acc: Record<string, any>, [name, value]) => {
            if (name && value) {
                if (name === SEARCH_STRING && typeof value === 'string') {
                    acc.searchString = value;
                    setSearchString(value);

                    return acc;
                }

                Object.entries(value ?? {}).forEach(([key, val]) => {
                    if (key === 'updatedAt' && Array.isArray(val)) {
                        acc[key] = {
                            selected: [key],
                            value: val.map((v: string) => (v ? new Date(v) : undefined)),
                        };
                    } else {
                        acc[key] = {
                            selected: [key],
                            value: val?.map((v: { data: any }) => ({ data: v.data })),
                        };
                    }
                });

                return acc;
            }

            return acc;
        }, {});

        if (valuesFromSaved) {
            setQueryWhere(valuesFromSaved);
        }
    };

export const ColumnPairs = {
    name: 'Наименование',
    methodology: 'Методология',
    stage: 'Этап',
    status: 'Статус',
    updatedAt: 'Дата изменения',
    authoredBy: 'Автор',
    subsidiaryName: 'Дочернее общество',
} as const;

export const TITLE_HEIGHT = 173;
export const defaultSet = {
    sortable: true,
    comparator: () => 0,
};
export const defaultRowStyle = {
    background: 'white',
};
export const projectListContext = {
    tableUniqueId: 'projectList',
};
